import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { ActionButton } from "@via/components/action-button/action-button";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import Drawer from "@via/components/drawer";
import {
  BannerSize,
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import Table, { DrawerSize } from "@via/components/table/table";
import TableContextMenu from "@via/components/table/table-context-menu";
import { useAuth } from "@via/hooks/useAuth";
import { useVmetaCanvasOwner } from "@via/hooks/useVmetaCanvasOwner";
import { useVmetaCanvas } from "@via/pages/projects/projects.api.tsx";
import {
  useDeleteVmetaConfigApi,
  useVmetaCollections,
  useVmetaConfigApis,
} from "@via/pages/projects/metadata/metadata.api.tsx";
import { BUTTONS, EMPTY_TABLE_MESSAGE, USER_ROLES } from "@via/utils/constants";
import { formatDateString } from "@via/utils/helpers";
import { createOneLineColumnFactory } from "@via/utils/table-utils";
import { VmetaConfigApi } from "common";
import { FC, useMemo, useState } from "react";
import ApiConfigForm from "@via/pages/projects/metadata/configuration/api-configs/api-config-form";

interface ApiConfigsProps {
  projectId: string;
  activeTab?: string;
}

type VmetaConfigApiTableRow = VmetaConfigApi & {
  targetCollectionName: string;
  endpointPath: string;
};

const columnHelper = createColumnHelper<VmetaConfigApiTableRow>();

const rowOptions = [
  { name: "View", href: "#" },
  { name: "Edit", href: "#" },
  { name: "Delete", href: "#" },
];

const ApiConfigs: FC<ApiConfigsProps> = ({ projectId }) => {
  const { user } = useAuth();
  const isAdmin =
    !!user && typeof user === "object" && user.userRole === USER_ROLES.ADMIN;
  const createOneLineColumn = createOneLineColumnFactory(columnHelper);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [viewConfig, setViewConfig] = useState<VmetaConfigApi | null>(null);
  const [editConfig, setEditConfig] = useState<VmetaConfigApi | null>(null);
  const [configToDelete, setConfigToDelete] = useState<{
    id: string;
    name: string;
    route: string;
  } | null>(null);

  const { data: canvasData, isLoading: isCanvasLoading } =
    useVmetaCanvas(projectId);
  const canvasOwner = canvasData?.data?.owner;
  const isOwner = useVmetaCanvasOwner(canvasOwner);
  const canWrite = isAdmin || !!canvasData?.data?.writeAllowed || isOwner;

  const { data: configApis, isLoading: isConfigApisLoading } =
    useVmetaConfigApis(projectId, page, pageSize);
  const { data: collections, isLoading: isCollectionsLoading } =
    useVmetaCollections(
      0,
      1000,
      [],
      [
        {
          id: "projectID",
          value: projectId,
        },
      ]
    );

  const { mutateAsync: deleteConfigApi, isPending: isDeleteConfigApiLoading } =
    useDeleteVmetaConfigApi();
  const isTableLoading =
    isCanvasLoading || isConfigApisLoading || isCollectionsLoading;

  const collectionNameById = useMemo(() => {
    return Object.fromEntries(
      (collections?.data ?? []).map(collection => [
        collection._id,
        collection.name,
      ])
    );
  }, [collections?.data]);

  const tableData = useMemo<VmetaConfigApiTableRow[]>(() => {
    return (configApis?.data ?? []).map(configApi => {
      const targetCollectionName =
        collectionNameById[configApi.collectionID] ?? configApi.collectionID;
      return {
        ...configApi,
        targetCollectionName,
        endpointPath: `/v1/vmeta/canvas/${projectId}/data/${targetCollectionName}/search/format/${configApi.route}`,
      };
    });
  }, [configApis?.data, collectionNameById, projectId]);

  const getRowOptions = () =>
    rowOptions.filter(option => {
      if (option.name === "Edit" || option.name === "Delete") {
        return canWrite;
      }
      return true;
    });

  const openCreateDrawer = () => {
    setViewConfig(null);
    setEditConfig(null);
    setIsCreateDrawerOpen(true);
  };

  const openViewDrawer = (configApi: VmetaConfigApi) => {
    setEditConfig(null);
    setViewConfig(configApi);
  };

  const openEditDrawer = (configApi: VmetaConfigApi) => {
    setViewConfig(null);
    setEditConfig(configApi);
  };

  const columns = [
    columnHelper.accessor("endpointPath", {
      header: () => <span className="px-1">Endpoint</span>,
      cell: info => (
        <button
          type="button"
          onClick={() => openViewDrawer(info.row.original)}
          className="via-link whitespace-nowrap px-1 text-left">
          {info.getValue()}
        </button>
      ),
    }),
    createOneLineColumn("route", "Route"),
    createOneLineColumn("targetCollectionName", "Target Collection"),
    createOneLineColumn("lastUpdateDate", "Updated At", {
      cellRenderer: value => formatDateString(value as Date, "LLL"),
    }),
    columnHelper.accessor("_id", {
      id: "options",
      header: () => "Options",
      cell: ({ row }) => {
        const options = getRowOptions();
        if (options.length === 0) return null;
        return (
          <TableContextMenu
            options={options}
            onSelect={option => {
              if (option.name === "View") {
                openViewDrawer(row.original);
              } else if (option.name === "Edit") {
                openEditDrawer(row.original);
              } else if (option.name === "Delete") {
                setConfigToDelete({
                  id: row.original._id,
                  name: row.original.endpointPath,
                  route: row.original.route,
                });
              }
            }}
          />
        );
      },
      enableSorting: false,
    }),
  ] as Array<ColumnDef<VmetaConfigApiTableRow>>;

  return (
    <>
      {!isCanvasLoading && !canWrite && (
        <div className="mt-4">
          <MessageBanner
            messages="Read-only mode. You can view API configs but cannot modify them."
            type={BannerType.Info}
            size={BannerSize.Small}
          />
        </div>
      )}
      <div className="flex justify-end -mt-6 relative top-11">
        {!isCanvasLoading && canWrite && (
          <ActionButton
            button={BUTTONS.NEW_API_CONFIG}
            onClick={openCreateDrawer}
          />
        )}
      </div>
      <div className="mt-6 overflow-auto h-100vh">
        <Table
          data={tableData}
          isLoading={isTableLoading}
          columns={columns}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          pageSizeOptions={[100]}
          total={configApis?.results || 0}
          enableGlobalFilter
          applyColumnSize
          customTableClassName="w-max whitespace-nowrap"
          emptyMessage={EMPTY_TABLE_MESSAGE.API_CONFIGS}
        />
      </div>

      <DeleteModal
        item={configToDelete}
        setItem={setConfigToDelete}
        deleteFunction={() =>
          deleteConfigApi({
            canvasId: projectId,
            id: configToDelete?.id ?? "",
          })
        }
        isLoading={isDeleteConfigApiLoading}
        title="Delete API Config"
        confirmText="Delete API Config"
      />

      <Drawer
        id="create-api-config"
        open={isCreateDrawerOpen}
        setOpen={setIsCreateDrawerOpen}
        size={DrawerSize.Big}
        resizable
        title="New API Config">
        {isCreateDrawerOpen ? (
          <ApiConfigForm
            canvasId={projectId}
            mode="create"
            initialConfig={null}
            setOpen={setIsCreateDrawerOpen}
          />
        ) : undefined}
      </Drawer>

      <Drawer
        id="edit-api-config"
        open={!!viewConfig}
        setOpen={() => setViewConfig(null)}
        size={DrawerSize.Big}
        resizable
        title="API Config">
        {viewConfig ? (
          <ApiConfigForm
            canvasId={projectId}
            mode="view"
            initialConfig={viewConfig}
            setOpen={() => setViewConfig(null)}
          />
        ) : undefined}
      </Drawer>

      <Drawer
        id="update-api-config"
        open={!!editConfig}
        setOpen={() => setEditConfig(null)}
        size={DrawerSize.Big}
        resizable
        title="Edit API Config">
        {editConfig ? (
          <ApiConfigForm
            canvasId={projectId}
            mode="edit"
            initialConfig={editConfig}
            setOpen={() => setEditConfig(null)}
          />
        ) : undefined}
      </Drawer>
    </>
  );
};

export default ApiConfigs;
